import { Box, Button, Typography, SxProps, Theme } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { compressImage } from "@shared/utils/compress-image";
import { ImageCropDialog } from "@features/UserProfile/ImageCropDialog";
import {
  formatMimetypeToFriendlyName,
  getAllowedFiles,
  MimetypeKeys,
} from "@shared/utils/mimetype";
import styled from "@emotion/styled";
import { useUIStore } from "@store/ui.store";
import { ImagePreviewField } from "./ImagePreviewField";

const ActionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  button {
    min-width: initial !important;
  }
`;

const ALLOWED_IMAGE_TYPES: Array<MimetypeKeys> = [
  "image/png",
  "image/jpeg",
  "image/jpg",
];

type ImageUploadWithPreviewProps = {
  label: string;
  imageSrc?: string | null;
  alt?: string;
  onSave: (data: { file: File }) => Promise<string>;
  onRemove: () => void;
  isLoading: boolean;
  buttonLabel?: string;
  cropTitle?: string;
  sx?: SxProps<Theme>;
  aspectRatio?: number;
  placeholderIcon?: React.ReactNode;
};

export function ImageUploadWithPreview({
  label,
  imageSrc,
  alt,
  onSave,
  onRemove,
  isLoading,
  buttonLabel = "Change",
  cropTitle = "Crop your image",
  sx,
  aspectRatio,
  placeholderIcon,
}: ImageUploadWithPreviewProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const confirmDialog = useUIStore((state) => state.confirmDialog);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      if (!file) return;

      if (!formatMimetypeToFriendlyName(file.type)) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        await confirmDialog({
          title: "File type not supported",
          content: (
            <div>
              <Typography mb={1}>
                Check the allowed image types below:
              </Typography>
              <Typography variant="body2">
                {getAllowedFiles(ALLOWED_IMAGE_TYPES).join(", ")}.
              </Typography>
            </div>
          ),
          confirmButtonText: "Close",
          cancelButtonText: false,
        });

        return;
      }

      setSelectedFile(file);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async (file: File) => {
    const compressed = await compressImage(file, { preset: "sm" });
    await onSave({ file: compressed });
    handleCancel();
  };

  const handleClickRemove: React.MouseEventHandler<HTMLButtonElement> =
    useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!imageSrc) return;
        onRemove();
      },
      [imageSrc, onRemove],
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: "auto",
        width: "100%",
        ...sx,
      }}
    >
      <ImagePreviewField
        label={label}
        alt={alt}
        imageSrc={imageSrc}
        aspectRatio={aspectRatio}
        placeholderIcon={placeholderIcon}
      />
      <ActionsContainer>
        <Button
          variant="outlined"
          component="label"
          size="small"
          fullWidth
          sx={{ padding: "0.25rem" }}
        >
          <input
            hidden
            accept={ALLOWED_IMAGE_TYPES.join(", ")}
            type="file"
            ref={fileInputRef}
            onChange={handleChangeFile}
          />
          {buttonLabel}
        </Button>
        <Button
          variant="text"
          size="small"
          fullWidth
          sx={{ padding: "0.25rem" }}
          onClick={handleClickRemove}
          disabled={!imageSrc || isLoading}
        >
          Remove
        </Button>
      </ActionsContainer>

      <ImageCropDialog
        title={cropTitle}
        file={selectedFile}
        open={!!selectedFile}
        isLoading={isLoading}
        onSave={handleSave}
        onCancel={handleCancel}
        aspect={aspectRatio}
      />
    </Box>
  );
}
